import React, { useState, useEffect, useRef } from 'react';
import { Button, Card, Progress, message, Space, Alert, Typography, Divider } from 'antd';
import { PlayCircleOutlined, CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { getContents, submitReading } from '../../api/learn';

// 文本差异对比函数
const highlightDifferences = (original, recognized) => {
  // 预处理：去除标点符号，转小写
  const cleanOriginal = original.replace(/[^\u4e00-\u9fa5]/g, '').toLowerCase();
  const cleanRecognized = recognized.replace(/[^\u4e00-\u9fa5]/g, '').toLowerCase();
  
  // 如果完全相同，直接返回
  if (cleanOriginal === cleanRecognized) {
    return { original, recognized, differences: [] };
  }
  
  // 使用简单的差异标记（实际项目中可以使用更复杂的算法）
  const wordsOriginal = original.split(/\s+/);
  const wordsRecognized = recognized.split(/\s+/);
  
  const differences = [];
  const maxLength = Math.max(wordsOriginal.length, wordsRecognized.length);
  
  for (let i = 0; i < maxLength; i++) {
    const wordOriginal = wordsOriginal[i] || '';
    const wordRecognized = wordsRecognized[i] || '';
    
    // 去除标点符号进行比较
    const cleanWordOriginal = wordOriginal.replace(/[^\w\u4e00-\u9fa5]/g, '').toLowerCase();
    const cleanWordRecognized = wordRecognized.replace(/[^\w\u4e00-\u9fa5]/g, '').toLowerCase();
    
    if (cleanWordOriginal !== cleanWordRecognized) {
      differences.push({
        index: i,
        original: wordOriginal,
        recognized: wordRecognized
      });
    }
  }
  
  return { original, recognized, differences };
};

const { Text } = Typography;

const Step1Reading = ({ onComplete }) => {
  const [contents, setContents] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [completedItems, setCompletedItems] = useState({});
  const [recording, setRecording] = useState(false);
  const [audioURL, setAudioURL] = useState(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const currentMimeTypeRef = useRef('audio/webm'); // 保存当前录音的 MIME 类型

  useEffect(() => {
    loadContents();
  }, []);

  const loadContents = async () => {
    try {
      const response = await getContents();
      if (response.code === 200) {
        setContents(response.data.contents);
      }
    } catch (error) {
      message.error('加载内容失败');
    }
  };

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });

      // 检查支持的 MIME 类型，优先使用 webm
      let mimeType = 'audio/webm;codecs=opus';
      if (!MediaRecorder.isTypeSupported(mimeType)) {
        mimeType = 'audio/webm';
        if (!MediaRecorder.isTypeSupported(mimeType)) {
          mimeType = 'audio/mp4';
          if (!MediaRecorder.isTypeSupported(mimeType)) {
            mimeType = ''; // 使用默认格式
          }
        }
      }

      const options = mimeType ? { mimeType } : {};
      const actualMimeType = mimeType || 'audio/webm'; // 保存实际使用的 MIME 类型
      mediaRecorderRef.current = new MediaRecorder(stream, options);
      audioChunksRef.current = [];
      currentMimeTypeRef.current = actualMimeType; // 保存引用

      mediaRecorderRef.current.ondataavailable = (event) => {
        audioChunksRef.current.push(event.data);
      };

      mediaRecorderRef.current.onstop = () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: currentMimeTypeRef.current });
        const url = URL.createObjectURL(audioBlob);
        setAudioURL(url);
        stream.getTracks().forEach(track => track.stop());
      };

      mediaRecorderRef.current.start();
      setRecording(true);
      setAudioURL(null);
      setResult(null);
      message.info('开始录音,请朗读准则内容...');
    } catch (error) {
      message.error('无法访问麦克风,请检查权限设置');
      console.error('录音失败:', error);
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && recording) {
      mediaRecorderRef.current.stop();
      setRecording(false);
      message.info('录音结束');
    }
  };

  const handleSubmit = async () => {
    if (!audioURL) {
      message.warning('请先录音');
      return;
    }

    try {
      setLoading(true);
      
      // 将audioURL转换为Blob
      const response = await fetch(audioURL);
      const blob = await response.blob();
      
      // 创建FormData - 根据实际 MIME 类型设置扩展名
      const mimeType = blob.type || currentMimeTypeRef.current;
      let extension = 'webm';
      if (mimeType.includes('mp4') || mimeType.includes('m4a')) {
        extension = 'm4a';
      } else if (mimeType.includes('mp3')) {
        extension = 'mp3';
      }
      const fileName = `recording.${extension}`;

      const formData = new FormData();
      formData.append('audio', blob, fileName);
      formData.append('item_number', currentContent.item_number);

      const result = await submitReading(formData);
      
      if (result.code === 200) {
        const { score, passed, recognized_text } = result.data;
        
        setResult({
          score,
          passed,
          recognizedText: recognized_text
        });

        if (passed) {
          message.success(`恭喜!准确率${score}%,已通过!`);
          
          // 记录已完成项
          const newCompleted = { ...completedItems };
          newCompleted[currentContent.item_number] = true;
          setCompletedItems(newCompleted);
          
          // 检查是否全部完成
          if (Object.keys(newCompleted).length === contents.length) {
            setTimeout(() => {
              message.success('跟读环节全部完成!');
              onComplete();
            }, 1500);
          } else {
            // 自动跳转到下一个
            setTimeout(() => {
              const nextIndex = contents.findIndex(
                (c, i) => i > currentIndex && !newCompleted[c.item_number]
              );
              if (nextIndex !== -1) {
                setCurrentIndex(nextIndex);
                setAudioURL(null);
                setResult(null);
              }
            }, 1500);
          }
        } else {
          message.warning(`准确率${score}%,未达到90%,请重新朗读`);
        }
      }
    } catch (error) {
      message.error(error.message || '提交失败');
    } finally {
      setLoading(false);
    }
  };

  const handlePlayAudio = () => {
    if (currentContent.audio_path) {
      const audio = new Audio(currentContent.audio_path);
      audio.play();
    } else {
      message.info('音频文件暂未上传');
    }
  };

  if (contents.length === 0) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>加载中...</div>;
  }

  const currentContent = contents[currentIndex];
  const completedCount = Object.keys(completedItems).length;
  const progressPercent = Math.round((completedCount / contents.length) * 100);

  return (
    <div className="step-reading">
      <div className="step-header">
        <Typography.Title level={3}>步骤一:跟读准则</Typography.Title>
        <Text type="secondary">请完整朗读以下准则（标题+内容），准确率达到90%即可通过</Text>
      </div>

      <Progress 
        percent={progressPercent} 
        status="active"
        format={() => `${completedCount}/${contents.length}`}
        style={{ marginBottom: 24 }}
      />

      <Card 
        className="content-card"
        title={
          <Space>
            <span>准则 {currentContent.item_number}</span>
            {completedItems[currentContent.item_number] && (
              <CheckCircleOutlined style={{ color: '#52c41a' }} />
            )}
          </Space>
        }
        extra={
          currentContent.audio_path && (
            <Button 
              icon={<PlayCircleOutlined />} 
              onClick={handlePlayAudio}
            >
              播放标准音频
            </Button>
          )
        }
      >
        <Alert
          message="请完整朗读以下内容"
          description={`请朗读："${currentContent.title}：${currentContent.content}"`}
          type="info"
          showIcon
          style={{ marginBottom: 16 }}
        />
        <div className="content-text">
          <Typography.Title level={4} style={{ display: 'inline' }}>{currentContent.title}：</Typography.Title>
          <Typography.Paragraph style={{ fontSize: 18, lineHeight: 2, display: 'inline' }}>
            {currentContent.content}
          </Typography.Paragraph>
        </div>
      </Card>

      <Card className="recording-card" title="录音区域" style={{ marginTop: 16 }}>
        <Space direction="vertical" style={{ width: '100%' }} size="large">
          <Space>
            <Button 
              type="primary" 
              size="large"
              onClick={recording ? stopRecording : startRecording}
              danger={recording}
            >
              {recording ? '停止录音' : '开始录音'}
            </Button>
            
            {audioURL && (
              <audio controls src={audioURL} style={{ marginLeft: 16 }} />
            )}
          </Space>

          {recording && (
            <Alert 
              message="正在录音中..." 
              description="请大声朗读准则内容"
              type="info"
              showIcon
            />
          )}

          {result && (
            <Card size="small">
              <Space direction="vertical" style={{ width: '100%' }}>
                <div>
                  {result.passed ? (
                    <CheckCircleOutlined style={{ fontSize: 24, color: '#52c41a' }} />
                  ) : (
                    <CloseCircleOutlined style={{ fontSize: 24, color: '#ff4d4f' }} />
                  )}
                  <Text style={{ fontSize: 18, marginLeft: 8 }}>
                    准确率: {result.score}%
                  </Text>
                </div>
                
                <div style={{ marginTop: 8 }}>
                  <div style={{ marginBottom: 8 }}>
                    <Text strong>原文:</Text>
                    <div style={{ padding: 8, backgroundColor: '#f6ffed', border: '1px solid #b7eb8f', borderRadius: 4, marginTop: 4 }}>
                      {currentContent.content}
                    </div>
                  </div>
                  
                  <div>
                    <Text strong>识别结果:</Text>
                    <div style={{ padding: 8, backgroundColor: '#fff7e6', border: '1px solid #ffd591', borderRadius: 4, marginTop: 4 }}>
                      {result.recognizedText}
                    </div>
                  </div>
                </div>
              </Space>
            </Card>
          )}

          <Button 
            type="primary" 
            size="large"
            onClick={handleSubmit}
            disabled={!audioURL}
            loading={loading}
            block
          >
            提交评分
          </Button>
        </Space>
      </Card>

      <div className="navigation" style={{ marginTop: 16, textAlign: 'center' }}>
        <Space>
          <Button 
            disabled={currentIndex === 0}
            onClick={() => {
              setCurrentIndex(currentIndex - 1);
              setAudioURL(null);
              setResult(null);
            }}
          >
            上一条
          </Button>
          <Text>{currentIndex + 1} / {contents.length}</Text>
          <Button 
            disabled={currentIndex === contents.length - 1}
            onClick={() => {
              setCurrentIndex(currentIndex + 1);
              setAudioURL(null);
              setResult(null);
            }}
          >
            下一条
          </Button>
        </Space>
      </div>
    </div>
  );
};

export default Step1Reading;
